// TripCard.tsx — Trip list card for TripsPage.
// Shows airline, route + dates + duration, passenger names, status badge, view e-ticket CTA.

import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/primitives';
import type { MockTrip, TripStatus } from '@/lib/trips-mock';

const F = '"Inter Variable","Inter",system-ui,sans-serif';
const DISPLAY = '"Fraunces Variable","Fraunces",Georgia,serif';

const STATUS_BADGE: Record<TripStatus, { label: string; color: 'green' | 'dim' | 'orange' }> = {
  confirmed: { label: 'Confirmed', color: 'green' },
  completed: { label: 'Completed', color: 'dim' },
  cancelled: { label: 'Cancelled', color: 'orange' },
};

interface TripCardProps {
  trip: MockTrip;
}

export function TripCard({ trip }: TripCardProps) {
  const navigate = useNavigate();
  const badge = STATUS_BADGE[trip.status];

  return (
    <div style={{
      background: '#131C30',
      borderRadius: 24,
      border: '1px solid rgba(255,255,255,0.07)',
      padding: '20px 24px',
      display: 'flex',
      alignItems: 'center',
      gap: 20,
    }}>
      {/* Airline logo circle */}
      <div style={{
        width: 52,
        height: 52,
        borderRadius: 16,
        background: trip.airlineColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: '#fff',
        fontFamily: F,
        fontSize: 14,
        fontWeight: 800,
        letterSpacing: -0.5,
      }}>
        {trip.airlineCode}
      </div>

      {/* Route + details */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Route */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <span style={{
            fontFamily: DISPLAY,
            fontSize: 28,
            fontWeight: 500,
            color: '#F4F3F0',
            lineHeight: 1,
            letterSpacing: -0.02,
          }}>{trip.from}</span>
          <svg width="20" height="12" viewBox="0 0 24 12" fill="none" style={{ flexShrink: 0 }}>
            <path d="M2 6h16M14 2l4 4-4 4" stroke="#8C98B0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M1 6c1.5-1.5 3-2 5-2h8" stroke="#8C98B0" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
          <span style={{
            fontFamily: DISPLAY,
            fontSize: 28,
            fontWeight: 500,
            color: '#F4F3F0',
            lineHeight: 1,
            letterSpacing: -0.02,
          }}>{trip.to}</span>
          <Badge color={badge.color === 'orange' ? 'orange' : badge.color === 'green' ? 'green' : 'dim'}>
            {badge.label}
          </Badge>
        </div>

        {/* Metadata row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}>
          <span style={{ fontFamily: F, fontSize: 13, color: '#8C98B0' }}>
            {trip.fromCity} → {trip.toCity}
          </span>
          <span style={{ fontFamily: F, fontSize: 13, color: '#8C98B0' }}>·</span>
          <span style={{ fontFamily: F, fontSize: 13, color: '#8C98B0' }}>
            {trip.departDate}{trip.returnDate ? ` → ${trip.returnDate}` : ''}
          </span>
          <span style={{ fontFamily: F, fontSize: 13, color: '#8C98B0' }}>·</span>
          <span style={{ fontFamily: F, fontSize: 13, color: '#8C98B0' }}>
            {trip.duration}
          </span>
        </div>

        {/* Passengers */}
        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4E5E78" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
          <span style={{ fontFamily: F, fontSize: 12, color: '#4E5E78' }}>
            {trip.passengers.join(', ')}
          </span>
        </div>
      </div>

      {/* Price + CTA */}
      <div style={{ flexShrink: 0, textAlign: 'right' }}>
        <div style={{ fontFamily: F, fontSize: 11, color: '#8C98B0', letterSpacing: 1, fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>
          PAID
        </div>
        <div style={{ fontFamily: DISPLAY, fontSize: 24, fontWeight: 500, color: '#F4F3F0', letterSpacing: -0.01 }}>
          ₹{trip.price.toLocaleString('en-IN')}
        </div>
        {trip.status !== 'cancelled' && (
          <button
            onClick={() => navigate(`/e-ticket/${trip.pnr}`)}
            style={{
              marginTop: 10,
              height: 36,
              padding: '0 16px',
              borderRadius: 999,
              background: 'transparent',
              color: '#E8441A',
              border: '1px solid rgba(232,68,26,0.4)',
              fontFamily: F,
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'rgba(232,68,26,0.1)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            View e-ticket →
          </button>
        )}
      </div>
    </div>
  );
}
